/**
 * Cross-promotion carousel for other Triardor products. Banner-sized,
 * horizontally scroll-snapping, clickable cards.
 *
 * Placeholder gradient banners for now — swap `img` in for real banner art
 * later (drop files in /public and set `image` on each product). Per-product
 * URLs are placeholders pointing at the Triardor site until set.
 */
import { useT } from '../lib/i18n';

interface Product {
  name: string;
  href: string;
  /** Tailwind gradient stops (brand tokens) used until a real banner exists. */
  gradient: string;
  /** Optional banner image path in /public (overrides the gradient). */
  image?: string;
}

// Only Triardor (the parent company → website). Other products are unreleased
// and intentionally not shown yet.
const PRODUCTS: Product[] = [
  { name: 'Triardor', href: 'https://triardor.com/en/', gradient: 'from-brand-navy to-brand-green' },
];

export function ProductsCarousel(): JSX.Element {
  const { t } = useT();
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-tg-hint">
        {t('products.title')}
      </h2>
      <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-1">
        {PRODUCTS.map((p) => (
          <a
            key={p.name}
            href={p.href}
            target="_blank"
            rel="noreferrer"
            className={
              'relative flex h-24 min-w-[80%] flex-none snap-start flex-col justify-end overflow-hidden rounded-card p-3 shadow-sm ' +
              (p.image ? 'bg-cover bg-center' : `bg-gradient-to-br ${p.gradient}`)
            }
            style={p.image ? { backgroundImage: `url(${p.image})` } : undefined}
          >
            <span className="text-base font-bold text-brand-cream drop-shadow">{p.name}</span>
            <span className="text-xs text-brand-cream/80">{t('products.cta')}</span>
          </a>
        ))}
      </div>
    </section>
  );
}
